// =============================================================================
// useNotifications — single source of truth para badges del sidebar
// y centro de notificaciones del TopBar.
//
// Provee:
//   - badges: { pedidos: number; inventario: number; reservas: number }
//   - inventoryAlerts: lista de items bajo mínimo (con dismiss persistente
//     en sessionStorage; "se borran al siguiente login")
//   - dismiss(id) / dismissAll()
//
// Refresca automáticamente vía SSE cuando cambian pedidos/inventario/reservas.
// =============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBranch } from '../context/branch-context';
import { useUser } from '../context/user-context';
import { listPedidos, type Pedido } from '../../api/pedidos';
import { listInventario, type InventarioItem } from '../../api/inventario';
import { listReservaciones, type Reservacion } from '../../api/reservaciones';
import { getAppSettings, SETTING_NOTIF_ROLES } from '../../api/settings';
import { useRealtime } from './use-realtime';
import { textoDe } from '../../i18n/textos';
import { textosComun } from '../../i18n/modulos/comun';

const DISMISSED_KEY = 'salonika.notif.dismissed.v1';
const DISMISSED_RESV_KEY = 'salonika.notif.dismissed-resv.v1';

function loadDismissedFromKey(key: string): Set<number> {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return new Set(arr.map(Number));
  } catch {}
  return new Set();
}
function saveDismissedToKey(key: string, s: Set<number>) {
  try {
    sessionStorage.setItem(key, JSON.stringify(Array.from(s)));
  } catch {}
}
const loadDismissed = () => loadDismissedFromKey(DISMISSED_KEY);
const saveDismissed = (s: Set<number>) => saveDismissedToKey(DISMISSED_KEY, s);

export interface InventoryAlert {
  id: number;
  nombre: string;
  cantidad: number;
  stock_minimo: number;
  unidad: string;
  severidad: 'agotado' | 'bajo';
}

/** Reserva próxima dentro de la siguiente hora — feed de notificaciones del bell. */
export interface ReservaAlert {
  id: number;
  cliente: string;
  personas: number;
  /** Hora ISO del slot. */
  fechaHora: string;
  /** Minutos hasta la reserva (negativo = ya pasó hace X min). */
  minutosHasta: number;
}

export interface NotificationsState {
  badges: {
    pedidos: number;
    inventario: number;
    reservas: number;
  };
  inventoryAlerts: InventoryAlert[];
  reservaAlerts: ReservaAlert[];
  unreadCount: number;
  dismiss: (id: number) => void;
  dismissAll: () => void;
  dismissReserva: (id: number) => void;
  refresh: () => void;
}

export function useNotifications(): NotificationsState {
  const { currentBranchId } = useBranch();
  const { user } = useUser();
  // Matriz "quién ve qué" (módulo Notificaciones, ago-2026). Semántica de
  // EXCLUSIÓN: un rol listado en notif_roles_config NO ve esa alerta; key/rol
  // ausente = la ve (default = comportamiento de siempre). El desarrollador
  // siempre ve todo.
  const [ocultas, setOcultas] = useState<{ inventario: string[]; reservas: string[]; pedidos: string[] }>({
    inventario: [], reservas: [], pedidos: [],
  });
  useEffect(() => {
    let vivo = true;
    getAppSettings()
      .then(s => {
        if (!vivo) return;
        try {
          const j = JSON.parse(s[SETTING_NOTIF_ROLES] || '{}');
          setOcultas({
            inventario: Array.isArray(j.inventario) ? j.inventario : [],
            reservas: Array.isArray(j.reservas) ? j.reservas : [],
            pedidos: Array.isArray(j.pedidos) ? j.pedidos : [],
          });
        } catch { /* JSON roto = default: todos ven todo */ }
      })
      .catch(() => { /* sin settings = default: todos ven todo */ });
    return () => { vivo = false; };
  }, []);
  const veAlerta = useCallback(
    (tipo: 'inventario' | 'reservas' | 'pedidos') =>
      !user || user.role === 'desarrollador' || !ocultas[tipo].includes(user.role),
    [user, ocultas],
  );
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [inventario, setInventario] = useState<InventarioItem[]>([]);
  const [reservas, setReservas] = useState<Reservacion[]>([]);
  const [dismissed, setDismissed] = useState<Set<number>>(loadDismissed);
  const [dismissedResv, setDismissedResv] = useState<Set<number>>(
    () => loadDismissedFromKey(DISMISSED_RESV_KEY)
  );

  const cargar = useCallback(async () => {
    if (!currentBranchId) return;
    try {
      const [p, inv, r] = await Promise.all([
        listPedidos({ sede_id: currentBranchId, activos: true }),
        listInventario(currentBranchId),
        listReservaciones({ sede_id: currentBranchId }),
      ]);
      setPedidos(p);
      setInventario(inv);
      setReservas(r);
    } catch (err) {
      console.warn('[useNotifications] load failed', err);
    }
  }, [currentBranchId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Tiempo real: recarga cuando cambia cualquiera de las 3 fuentes.
  useRealtime(
    [
      'pedido_creado', 'pedido_actualizado', 'pedido_eliminado',
      'inventario_actualizado', 'inventario_movimiento',
      'reservacion_creada', 'reservacion_actualizada',
      'venta_creada',
    ],
    () => cargar()
  );

  // --- Conteos ---
  const hoyStr = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const badges = useMemo(() => {
    // Pedidos activos = todos los que no están facturados ni cancelados.
    // listPedidos({activos:true}) ya filtra en backend pero por si acaso.
    const pedidosCount = pedidos.filter(
      p => p.estado !== 'facturado' && p.estado !== 'cancelado'
    ).length;

    const inventarioCount = inventario.filter(
      i => Number(i.cantidad) <= Number(i.stock_minimo)
    ).length;

    const reservasCount = reservas.filter(
      r => r.fecha_hora.slice(0, 10) === hoyStr
    ).length;

    return {
      pedidos: veAlerta('pedidos') ? pedidosCount : 0,
      inventario: veAlerta('inventario') ? inventarioCount : 0,
      reservas: veAlerta('reservas') ? reservasCount : 0,
    };
  }, [pedidos, inventario, reservas, hoyStr, veAlerta]);

  // --- Alertas de inventario (con dismiss) ---
  const inventoryAlerts = useMemo<InventoryAlert[]>(() => {
    if (!veAlerta('inventario')) return [];
    return inventario
      .filter(i => Number(i.cantidad) <= Number(i.stock_minimo))
      .filter(i => !dismissed.has(i.id))
      .map((i): InventoryAlert => ({
        id: i.id,
        nombre: i.nombre,
        cantidad: Number(i.cantidad),
        stock_minimo: Number(i.stock_minimo),
        unidad: i.unidad,
        severidad: Number(i.cantidad) <= 0 ? 'agotado' : 'bajo',
      }))
      // Agotados primero, luego más críticos
      .sort((a, b) => {
        if (a.severidad !== b.severidad) return a.severidad === 'agotado' ? -1 : 1;
        const ratioA = a.cantidad / Math.max(0.0001, a.stock_minimo);
        const ratioB = b.cantidad / Math.max(0.0001, b.stock_minimo);
        return ratioA - ratioB;
      });
  }, [inventario, dismissed, veAlerta]);

  const dismiss = useCallback((id: number) => {
    setDismissed(prev => {
      const next = new Set(prev); next.add(id);
      saveDismissed(next);
      return next;
    });
  }, []);

  const dismissAll = useCallback(() => {
    setDismissed(prev => {
      const next = new Set(prev);
      inventoryAlerts.forEach(a => next.add(a.id));
      saveDismissed(next);
      return next;
    });
  }, [inventoryAlerts]);

  // --- Alertas de reservas próximas (próximos 60 min) ---
  // Ticking minuto a minuto: el bell mantiene el "ahora" actualizado para que
  // el countdown ("en 12 min", "en 3 min") refleje el paso del tiempo sin
  // depender de una recarga manual.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const reservaAlerts = useMemo<ReservaAlert[]>(() => {
    if (!veAlerta('reservas')) return [];
    return reservas
      .filter(r => {
        if (r.estado === 'cancelada' || r.estado === 'cumplida') return false;
        if (dismissedResv.has(r.id)) return false;
        const ts = new Date(r.fecha_hora).getTime();
        if (Number.isNaN(ts)) return false;
        const min = (ts - now) / 60000;
        // Ventana: la reserva aparece si está en los próximos 60 min
        // (futuro) o hace menos de 10 min (post-llegada). Después del +10
        // sale sola; antes del +10 sólo sale si el usuario la dismiss-ea
        // manualmente desde el bell. Pedido usuario jun-2026.
        return min >= -10 && min <= 60;
      })
      .map((r): ReservaAlert => {
        const ts = new Date(r.fecha_hora).getTime();
        return {
          id: r.id,
          cliente: r.cliente_nombre || textoDe(textosComun, 'sinNombre'),
          personas: Number(r.personas) || 0,
          fechaHora: r.fecha_hora,
          minutosHasta: Math.round((ts - now) / 60000),
        };
      })
      .sort((a, b) => a.minutosHasta - b.minutosHasta);
  }, [reservas, now, dismissedResv, veAlerta]);

  const dismissReserva = useCallback((id: number) => {
    setDismissedResv(prev => {
      const next = new Set(prev); next.add(id);
      saveDismissedToKey(DISMISSED_RESV_KEY, next);
      return next;
    });
  }, []);

  return {
    badges,
    inventoryAlerts,
    reservaAlerts,
    unreadCount: inventoryAlerts.length + reservaAlerts.length,
    dismiss,
    dismissAll,
    dismissReserva,
    refresh: cargar,
  };
}

/** Helper para limpiar dismissed al hacer logout. */
export function clearNotificationsDismissed() {
  try {
    sessionStorage.removeItem(DISMISSED_KEY);
    sessionStorage.removeItem(DISMISSED_RESV_KEY);
  } catch {}
}
